import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

/**
 * Page navigation for report data table.
 */
const ReportPaginator = ({ currentPage, totalPages, totalRows, pageSize, onPageChange }) => {
  if (totalPages <= 1) return null;

  const startRow = (currentPage - 1) * pageSize + 1;
  const endRow = Math.min(currentPage * pageSize, totalRows);

  // Build page number buttons — show up to 7 pages with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');

      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);

      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const btnStyle = (active) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minWidth: '28px', height: '28px', padding: '0 6px',
    borderRadius: '4px', fontSize: '12px', fontWeight: active ? 700 : 400,
    border: active ? '1px solid var(--color-primary)' : '1px solid var(--color-gray-300)',
    background: active ? 'var(--color-primary-light)' : '#fff',
    color: active ? 'var(--color-primary)' : 'var(--color-gray-700)',
    cursor: 'pointer'
  });

  const navBtnStyle = (disabled) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: '28px', height: '28px',
    borderRadius: '4px', border: '1px solid var(--color-gray-300)',
    background: disabled ? 'var(--color-gray-100)' : '#fff',
    color: disabled ? 'var(--color-gray-400)' : 'var(--color-gray-700)',
    cursor: disabled ? 'not-allowed' : 'pointer'
  });

  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '8px 12px', marginTop: '4px'
    }}>
      <span style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
        Showing {startRow.toLocaleString()}-{endRow.toLocaleString()} of {totalRows.toLocaleString()}
      </span>

      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        {/* First page */}
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={navBtnStyle(currentPage === 1)}
          title="First page"
        >
          <ChevronsLeft size={14} />
        </button>

        {/* Previous */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={navBtnStyle(currentPage === 1)}
          title="Previous page"
        >
          <ChevronLeft size={14} />
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((page, i) => (
          page === '...' ? (
            <span key={`ellipsis-${i}`} style={{ padding: '0 4px', color: 'var(--color-gray-400)', fontSize: '12px' }}>
              ...
            </span>
          ) : (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              style={btnStyle(page === currentPage)}
            >
              {page}
            </button>
          )
        ))}

        {/* Next */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={navBtnStyle(currentPage === totalPages)}
          title="Next page"
        >
          <ChevronRight size={14} />
        </button>

        {/* Last page */}
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          style={navBtnStyle(currentPage === totalPages)}
          title="Last page"
        >
          <ChevronsRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default ReportPaginator;
